import { useTranslation } from 'react-i18next'
import {
  DashboardReason,
  Reason,
  ReturnReasonsSettings
} from '@/domain/return-reasons'
import {
  FormProps,
  SettingsPanel,
  SwitchRow
} from '@/ui/components/group-settings'
import {
  InstructionsBanner,
  ReasonsWithChildren,
  ReasonsWithoutChildren
} from '@/ui/components/reasons'

// The reasons as the portal page edits them, so the group and each portal look the same.
export const ReasonsForm: React.FC<FormProps<ReturnReasonsSettings>> = ({
  value,
  readOnly,
  onChange
}) => {
  const { t } = useTranslation()
  const indexed: DashboardReason[] = value.reasons.map((reason, index) => ({
    ...reason,
    index
  }))
  const update = (
    patch: Partial<Reason>,
    reasonIndex: number,
    subReasonIndex?: number
  ) =>
    onChange({
      ...value,
      reasons: value.reasons.map((reason, i) =>
        i !== reasonIndex
          ? reason
          : subReasonIndex === undefined
            ? { ...reason, parentReason: { ...reason.parentReason, ...patch } }
            : {
                ...reason,
                subReasons: reason.subReasons.map((sub, j) =>
                  j === subReasonIndex ? { ...sub, ...patch } : sub
                )
              }
      )
    })
  const handlers = {
    handleChangeCategoryIds: (
      categoryIds: string[],
      reasonIndex: number,
      subReasonIndex?: number
    ) => update({ categoryIds }, reasonIndex, subReasonIndex),
    handleChangeMandatoryComment: (
      mandatoryComment: boolean,
      reasonIndex: number,
      subReasonIndex?: number
    ) => update({ mandatoryComment }, reasonIndex, subReasonIndex)
  }

  return (
    <>
      <InstructionsBanner />
      {value.randomizeOrder === undefined ? null : (
        <SettingsPanel>
          <SwitchRow
            label={t('return_reasons.randomize_order')}
            description={t('return_reasons.randomize_order_description')}
            checked={value.randomizeOrder}
            readOnly={readOnly}
            onChange={(randomizeOrder) =>
              onChange({ ...value, randomizeOrder })
            }
          />
        </SettingsPanel>
      )}
      {value.reasons.length ? (
        <div>
          {/* Every platform can limit a reason to some categories, as in the backoffice. */}
          <ReasonsWithoutChildren
            reasons={indexed.filter((reason) => !reason.subReasons.length)}
            readOnly={readOnly}
            isShopify
            {...handlers}
          />
          {indexed
            .filter((reason) => reason.subReasons.length)
            .map((reason) => (
              <ReasonsWithChildren
                key={reason.index}
                index={reason.index}
                parentReason={reason.parentReason}
                reasons={reason.subReasons}
                readOnly={readOnly}
                isShopify
                handleChangeHidden={(hidden, reasonIndex) =>
                  update({ hidden }, reasonIndex)
                }
                {...handlers}
              />
            ))}
        </div>
      ) : (
        <p className="text-sm text-neutral-600">
          {t('returns_refunds.reasons.empty')}
        </p>
      )}
    </>
  )
}
